import React, { useState } from "react";
import { Cpu, Zap, ShieldAlert, ShieldCheck, CheckCircle2, XCircle, ArrowRight, Gauge, Layers, Server } from "lucide-react";
import { Link } from "react-router-dom";

const SovereignMeshComparison = () => {
  const [activeTab, setActiveTab] = useState("panthm");
  const [concurrentStreams, setConcurrentStreams] = useState(5000);

  // Dynamic telemetry calculations
  const panthmLCP = 0.8;
  const legacyLCP = (2.8 + (concurrentStreams / 10000) * 1.2).toFixed(1);

  const panthmAudioLatency = 340;
  const legacyAudioLatency = Math.min(3200, 1850 + Math.round((concurrentStreams / 5000) * 350));

  const panthmMonthlyCost = (1800 + (concurrentStreams / 5000) * 400).toLocaleString();
  const legacyMonthlyCost = (14500 + (concurrentStreams / 5000) * 3200).toLocaleString();

  return (
    <section className="py-24 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white relative overflow-hidden border-t border-slate-200 dark:border-slate-800" id="sovereign-mesh">
      {/* Glow */}
      <div className="absolute top-1/3 -right-32 w-96 h-96 bg-primary/10 dark:bg-primary/20 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 -left-32 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none" />

      <div className="wrapper relative z-10 space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
            <Cpu size={14} /> Proprietary Enterprise IP
          </div>
          <h2 data-aos="fade-up" className="section-heading text-slate-900 dark:text-white">
            PANTHM Sovereign AI Mesh™ (PSAM) vs. Legacy IT
          </h2>
          <p data-aos="fade-up" className="text-slate-600 dark:text-slate-300 text-base sm:text-lg">
            Compare the architecture of 30-year legacy IT service providers against PANTHM’s zero-middleware, sovereign AI engineering stack.
          </p>

          {/* Interactive Toggle */}
          <div className="flex justify-center pt-4">
            <div className="inline-flex p-1 rounded-2xl bg-slate-200/80 dark:bg-slate-900 border border-slate-300 dark:border-slate-800 shadow-inner">
              <button
                type="button"
                onClick={() => setActiveTab("panthm")}
                className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
                  activeTab === "panthm"
                    ? "bg-primary text-white shadow-lg shadow-primary/30"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <Zap size={15} /> PANTHM Sovereign Mesh™
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("legacy")}
                className={`px-5 py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all flex items-center gap-2 ${
                  activeTab === "legacy"
                    ? "bg-red-600 dark:bg-red-500/20 text-white dark:text-red-300 border border-red-500/40 shadow-lg"
                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <Layers size={15} /> Legacy IT Agency Model
              </button>
            </div>
          </div>
        </div>

        {/* Live Architecture Comparison Cards */}
        <div className="grid lg:grid-cols-12 gap-8 items-stretch">
          {/* Telemetry Dashboard */}
          <div className="lg:col-span-5 bg-white dark:bg-slate-900/80 p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl dark:shadow-2xl flex flex-col justify-between space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold flex items-center gap-1.5">
                  <Gauge size={14} className="text-primary" /> Live Telemetry Simulator
                </span>
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-mono font-bold bg-primary/10 dark:bg-primary/20 text-primary">
                  Interactive
                </span>
              </div>

              {/* Slider for Concurrent Load */}
              <div className="space-y-2 pt-2">
                <div className="flex justify-between text-xs font-semibold">
                  <span className="text-slate-700 dark:text-slate-300">Concurrent User / Call Streams</span>
                  <span className="font-mono text-primary font-bold">{concurrentStreams.toLocaleString()} Streams</span>
                </div>
                <input
                  type="range"
                  min="500"
                  max="50000"
                  step="500"
                  value={concurrentStreams}
                  onChange={(e) => setConcurrentStreams(parseInt(e.target.value))}
                  className="w-full accent-primary h-2 bg-slate-200 dark:bg-slate-800 rounded-lg cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                  <span>500 Streams</span>
                  <span>25k Streams</span>
                  <span>50k Streams</span>
                </div>
              </div>

              {/* Metrics Grid */}
              <div className="space-y-3 pt-4">
                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm dark:shadow-none">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">Voice Latency (WebRTC)</div>
                    <div className={`text-2xl font-mono font-extrabold mt-0.5 ${
                      activeTab === "panthm" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
                    }`}>
                      {activeTab === "panthm" ? `<${panthmAudioLatency}ms` : `${legacyAudioLatency}ms`}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">
                    {activeTab === "panthm" ? "Human-cadence conversational" : "Noticeable robotic pause"}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm dark:shadow-none">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">Web Page Rendering (LCP)</div>
                    <div className={`text-2xl font-mono font-extrabold mt-0.5 ${
                      activeTab === "panthm" ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"
                    }`}>
                      {activeTab === "panthm" ? `${panthmLCP}s` : `${legacyLCP}s`}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">
                    {activeTab === "panthm" ? "0ms Speculation Prerendered" : "Monolithic Drupal/Java"}
                  </span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm dark:shadow-none">
                  <div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 font-medium">Est. Monthly Cloud Compute</div>
                    <div className={`text-2xl font-mono font-extrabold mt-0.5 ${
                      activeTab === "panthm" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"
                    }`}>
                      ${activeTab === "panthm" ? panthmMonthlyCost : legacyMonthlyCost}
                    </div>
                  </div>
                  <span className="text-xs text-slate-500 font-medium">
                    {activeTab === "panthm" ? "Zero SaaS middleware tax" : "Heavy third-party licensing"}
                  </span>
                </div>
              </div>
            </div>

            <div className="pt-2 text-xs text-slate-500 text-center">
              *Telemetry modeled on production stress tests and WebRTC benchmarks.
            </div>
          </div>

          {/* Architecture Comparison Specs */}
          <div className="lg:col-span-7 bg-white dark:bg-slate-900/60 backdrop-blur-xl p-6 sm:p-8 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl dark:shadow-2xl flex flex-col justify-between space-y-6">
            <div className="space-y-6">
              <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800">
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                    {activeTab === "panthm" ? "PANTHM Sovereign AI Mesh™ Architecture" : "Legacy Enterprise IT Agency Stack"}
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 mt-1">
                    {activeTab === "panthm"
                      ? "Direct kernel-level streaming, single-tenant data sovereignty, zero middleware."
                      : "Multi-layered monolithic silos, heavy middleware adapters, multi-month delivery."}
                  </p>
                </div>
                <div className={`p-2 rounded-2xl ${
                  activeTab === "panthm" ? "bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-red-500/10 dark:bg-red-500/20 text-red-600 dark:text-red-400"
                }`}>
                  {activeTab === "panthm" ? <ShieldCheck size={28} /> : <ShieldAlert size={28} />}
                </div>
              </div>

              {/* Feature Points */}
              <div className="space-y-3.5">
                {activeTab === "panthm" ? (
                  <>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-emerald-500/20">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">Zero Middleware Tax:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Direct WebRTC audio bridges and database triggers eliminate expensive Zapier, Make, and middleware subscriptions.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-emerald-500/20">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">2-4 Week Rapid Production Sprint:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Turnkey engineering delivered in weeks, not 6-month bloated billing cycles.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-emerald-500/20">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">Zero Data Retention (ZDR) Privacy:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Your proprietary data, weights, and client transcripts never train third-party public models.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-emerald-500/20">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">Machine-to-Machine (MCP) Native:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Fully equipped with Model Context Protocol for automated AI agent orchestration.</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-red-500/20">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">Compounding Middleware Inefficiencies:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Heavily reliant on chained SaaS connectors, inflating latency to 1,500ms+ and multiplying point-of-failure risks.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-red-500/20">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">6-12 Month Governance & SOW Lag:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Excessive middle-management staffing and bureaucratic change requests delay deployments.</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950/60 border border-slate-200 dark:border-red-500/20">
                      <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-sm font-bold text-slate-900 dark:text-white">Multi-Tenant Cloud Data Exposure:</span>
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 leading-relaxed">Client data frequently passes through unvetted shared cloud APIs without strict ZDR guarantees.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <span className="text-xs text-slate-500 dark:text-slate-400">Ready to modernize your infrastructure?</span>
              <Link
                to="/contact"
                className="w-full sm:w-auto px-6 py-3 rounded-xl primary-btn text-white text-xs sm:text-sm font-semibold flex items-center justify-center gap-2 shadow-lg shadow-primary/30"
              >
                <span>Deploy with Sovereign Mesh</span>
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SovereignMeshComparison;
